import mustache from 'mustache';
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';

import { Loader } from '@/components/core/Loader';
import { LOCAL_STORAGE } from '@/constants';
import { logger } from '@/services/logger';

import { useIndexedDB } from './useIndexedDB';

interface Translation {
    [key: string]: string;
}

interface CachedTranslationObject {
    languageKey: string,
    map: {
        languageCode: string;
        translations: Translation;
    }
}

interface TranslationContextInterface {
    t: (key: string,
        interpolationValues?: Record<string, string | number>,
        interpolationType?: 'messageformat') => string;
}

const TranslationContext = createContext<TranslationContextInterface | undefined>(undefined);

/**
 * A somewhat dumb translation hook that mimics react-i18next API that grabs
 * the translations from the legacy app using localStorage and indexed db.
 *
 * This hook would be updated in the final processes of the migration.
 */
const useTranslation = () => {
    const translationsContext = useContext(TranslationContext);

    if (!translationsContext) {
        throw new Error('useTranslation must be used within a TranslationProvider');
    }

    return translationsContext;
};

function TranslationProvider({ children }: PropsWithChildren<Record<never, never>>) {

    const [translations, setTranslations] = useState<Translation>({});
    const [loading, setLoading] = useState(true);
    const { get: getCachedTranslations } = useIndexedDB<CachedTranslationObject>('translations');

    useEffect(() => {

        const fetchTranslations = async () => {
            try {
                const languageKey = localStorage.getItem(LOCAL_STORAGE.LANGUAGE_KEY);

                if (languageKey) {
                    const cachedTranslations = await getCachedTranslations(languageKey);
                    if (cachedTranslations && cachedTranslations.map.translations) {
                        logger.debug('TranslationProvider: Successfully loaded cached translations');
                        setTranslations(cachedTranslations.map.translations);
                    }
                } else {
                    logger.error(`TranslationProvider: Language key was not found in local storage ${languageKey}`);
                }

                setLoading(false);
            } catch (error) {
                logger.error('TranslationProvider: Error fetching translations from IndexedDB:', error);
                setLoading(false);
            }
        };

        fetchTranslations();

    }, []);

    const t = useCallback((key: string, interpolationValues?: Record<string, string | number>, interpolationType?: 'messageformat') => {
        //Handle undefined keys to prevent mustache render errors
        if (!key) {
            return '';
        }

        const translation = translations[key] || key;

        // To retain backward compatibility with Angular-Translate, we have to support the 'messageformat' used
        // to translate validation messages.
        // See more: https://angular-translate.github.io/docs/#/guide/14_pluralization:~:text=Overriding%20interpolation%20temporarily
        try {
            if (interpolationType === 'messageformat') {
                return mustache.render(translation, interpolationValues, {}, ['{', '}']);
            } else {
                return mustache.render(translation, interpolationValues);
            }
        } catch (error) {
            logger.warn(`Failed to parse translation template for key "${key}":`, error);
            return translation;
        }
    }, [translations]);

    if (loading) {
        return <Loader />;
    }

    return <TranslationContext.Provider value={{ t }}>{children}</TranslationContext.Provider>;
};

export { TranslationContext, TranslationProvider, useTranslation };
